import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { FiAlertTriangle, FiArrowRight } from 'react-icons/fi';
import Button from '../../../../component/ui/Button';
import { getDate } from '../../../../utils/dateUtils';
import './SellLooseRecalculationModal.scss';

const formatUnit = (qty, unitName) => {
	if (!unitName) return '';
	const clean = String(unitName).trim();
	const num = Number(qty);
	if (num === 1) {
		return clean.toLowerCase().endsWith('s') && clean.length > 2
			? clean.slice(0, -1).toLowerCase()
			: clean.toLowerCase();
	} else {
		return clean.toLowerCase().endsWith('s')
			? clean.toLowerCase()
			: `${clean.toLowerCase()}s`;
	}
};

const formatExpiry = (expiry) => {
	if (!expiry) return '—';
	if (typeof expiry === 'string' && /^\d{2}\/\d{2}$/.test(expiry.trim())) {
		return expiry.trim();
	}
	if (typeof expiry === 'string' && /^\d{2}\/\d{4}$/.test(expiry.trim())) {
		const [mm, yyyy] = expiry.trim().split('/');
		return `${mm}/${yyyy.slice(-2)}`;
	}
	const formatted = getDate(expiry, false, false, true);
	return formatted && formatted !== 'N/A' ? formatted : expiry;
};

const SellLooseRecalculationModal = ({
	isOpen,
	onClose,
	onCancel,
	onConfirmRecalculate,
	onDontRecalculate,
	productName = '',
	productCode = '',
	contains = 1,
	packagingName = 'strip',
	unitName = 'tablet',
	currentSellLoose = false,
	pendingSellLoose = true,
	batches = [],
	loader = false,
}) => {
	const handleCancel = React.useCallback(() => {
		if (onCancel) {
			onCancel();
		} else if (onClose) {
			onClose();
		}
	}, [onCancel, onClose]);

	useEffect(() => {
		if (!isOpen) return;
		const handleKeyDown = (e) => {
			if (e.key === 'Escape' && !loader) {
				handleCancel();
			}
		};
		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [isOpen, loader, handleCancel]);

	useEffect(() => {
		if (isOpen) {
			document.body.style.overflow = 'hidden';
		} else {
			document.body.style.overflow = '';
		}
		return () => {
			document.body.style.overflow = '';
		};
	}, [isOpen]);

	if (!isOpen) return null;

	const packSize = Number(contains) || 1;
	const isEnabling = Boolean(pendingSellLoose);

	const packUnitSingular = formatUnit(1, packagingName || 'strip');
	const looseUnitSingular = formatUnit(1, unitName || 'tablet');
	const packConversionStr = `1 ${packUnitSingular} = ${packSize} ${formatUnit(packSize, looseUnitSingular)}`;

	const rawAffected = (batches || []).filter(
		(b) => b && b.batch_no && String(b.batch_no).trim() !== ''
	);
	const affectedBatches = rawAffected.some((b) => Number(b.quantity || 0) > 0)
		? rawAffected.filter((b) => Number(b.quantity || 0) > 0)
		: rawAffected;

	const calculatedBatches = affectedBatches.map((batch) => {
		const currentQty = Number(batch.quantity || 0);
		let newQty;
		if (isEnabling) {
			newQty = Math.round(currentQty * packSize);
		} else {
			newQty = parseFloat((currentQty / packSize).toFixed(2));
		}
		return {
			...batch,
			currentQty,
			newQty,
		};
	});

	const totalCurrent = calculatedBatches.reduce((acc, b) => acc + b.currentQty, 0);
	const totalNew = isEnabling
		? Math.round(totalCurrent * packSize)
		: parseFloat((totalCurrent / packSize).toFixed(2));

	const currentUnitName = isEnabling ? packagingName || 'strip' : unitName || 'tablet';
	const newUnitName = isEnabling ? unitName || 'tablet' : packagingName || 'strip';

	const currentSettingType = isEnabling ? 'Packed quantity' : 'Loose quantity';
	const newSettingType = isEnabling ? 'Loose quantity' : 'Packed quantity';

	const currentDisplay = `${totalCurrent} ${formatUnit(totalCurrent, currentUnitName)}`;
	const newDisplay = `${totalNew} ${formatUnit(totalNew, newUnitName)}`;

	const currentEquivalent = `≈ ${totalNew} ${formatUnit(totalNew, newUnitName)} equivalent`;
	const newEquivalent = `≈ ${totalCurrent} ${formatUnit(totalCurrent, currentUnitName)} equivalent`;

	const explanationText = isEnabling
		? 'Enabling Sell Loose will convert the stock quantity from packed units to loose units. Physical stock does not change, only how it is stored.'
		: 'Disabling Sell Loose will convert the stock quantity from loose units to packed units. Physical stock does not change, only how it is stored.';

	const calculationFormula = isEnabling
		? `Loose qty = Packed qty × Pack size → ${totalCurrent} × ${packSize} = ${totalNew} ${formatUnit(totalNew, unitName || 'tablet')}`
		: `Packed qty = Loose qty ÷ Pack size → ${totalCurrent} ÷ ${packSize} = ${totalNew} ${formatUnit(totalNew, packagingName || 'strip')}`;

	return (
		<div
			className="modal show d-block sell-loose-recalculation-modal"
			tabIndex="-1"
			role="dialog"
			aria-modal="true"
			onClick={(e) => {
				if (e.target === e.currentTarget && !loader) {
					handleCancel();
				}
			}}
		>
			<div
				className="modal-dialog modal-dialog-centered top-0"
				onClick={(e) => e.stopPropagation()}
			>
				<div className="modal-content sell-loose-modal-content">
					{/* Modal Header */}
					<div className="sell-loose-modal-header">
						<div className="sell-loose-header-left">
							<div className="sell-loose-icon-box">
								<FiAlertTriangle size={18} />
							</div>
							<div className="sell-loose-header-text">
								<h5 className="sell-loose-modal-title">
									Confirm Stock Quantity Recalculation
								</h5>
								<p className="sell-loose-modal-subtitle">
									The Sell Loose setting has changed. Stock will be recalculated using
									the pack conversion value. Please review before proceeding.
								</p>
							</div>
						</div>
						<div className="sell-loose-header-right">
							<Button
								type="button"
								className="mt-0 btn-danger add-pro"
								button=""
								close
								onClick={handleCancel}
								disabled={loader}
							/>
						</div>
					</div>

					{/* Modal Body */}
					<div className="modal-body sell-loose-modal-body">
						{/* Product & Pack Conversion Meta Row */}
						<div className="sell-loose-meta-row">
							<div className="meta-left">
								<div className="meta-label">Product</div>
								<div className="product-name" title={productName}>
									{productName || '—'}
								</div>
								{productCode && (
									<div className="product-code">
										Code: {productCode}
									</div>
								)}
							</div>
							<div className="meta-right">
								<div className="meta-label">Pack conversion</div>
								<div className="pack-conversion">
									{packConversionStr}
								</div>
							</div>
						</div>

						{/* Comparison Cards Row */}
						<div className="sell-loose-cards-row">
							<div className="setting-card current-card">
								<div className="card-badge current">CURRENT SETTING</div>
								<div className="card-type current">{currentSettingType}</div>
								<div className="card-qty current">{currentDisplay}</div>
								<div className="card-equivalent current">{currentEquivalent}</div>
							</div>

							<div className="cards-arrow">
								<FiArrowRight size={22} color="#0d9488" />
							</div>

							<div className="setting-card new-card">
								<div className="card-badge new">NEW SETTING</div>
								<div className="card-type current">{newSettingType}</div>
								<div className="card-qty current">{newDisplay}</div>
								<div className="card-equivalent current">{newEquivalent}</div>
							</div>
						</div>

						{/* What will happen? Box */}
						<div className="sell-loose-info-box">
							<div className="info-box-title">What will happen?</div>
							<div className="info-box-text">{explanationText}</div>
							<div className="info-box-formula">{calculationFormula}</div>
						</div>

						{/* Affected Batches Table */}
						<div className="sell-loose-batches-section">
							<div className="batches-title">
								Affected batches ({calculatedBatches.length})
							</div>
							<div className="batches-table-wrapper">
								<table className="batches-table">
									<thead>
										<tr>
											<th>Batch No</th>
											<th className="text-center">Expiry</th>
											<th className="text-right">Current qty</th>
											<th className="text-right">New qty</th>
										</tr>
									</thead>
									<tbody>
										{calculatedBatches.length > 0 ? (
											calculatedBatches.map((batch, idx) => (
												<tr key={batch.product_batch_id || batch.batch_no || idx}>
													<td className="batch-no">{batch.batch_no}</td>
													<td className="expiry">{formatExpiry(batch.expiry_date)}</td>
													<td className="current-qty">
														{batch.currentQty} {formatUnit(batch.currentQty, currentUnitName)}
													</td>
													<td className="new-qty">
														{batch.newQty} {formatUnit(batch.newQty, newUnitName)}
													</td>
												</tr>
											))
										) : (
											<tr>
												<td colSpan="4" className="text-center py-2 text-muted">
													No affected batches found
												</td>
											</tr>
										)}
									</tbody>
									{calculatedBatches.length > 0 && (
										<tfoot>
											<tr>
												<td>Total</td>
												<td></td>
												<td className="text-right">
													{totalCurrent} {formatUnit(totalCurrent, currentUnitName)}
												</td>
												<td className="text-right">
													{totalNew} {formatUnit(totalNew, newUnitName)}
												</td>
											</tr>
										</tfoot>
									)}
								</table>
							</div>
							<p className="batches-disclaimer">
								Previously recorded invoices and purchase transactions will not be changed.
								Values shown are illustrative; actual figures use the saved stock and pack
								configuration.
							</p>
						</div>
					</div>

					{/* Modal Footer */}
					<div className="modal-footer sell-loose-modal-footer">
						<Button
							type="button"
							button="Cancel"
							className="btn-gray mt-0 px-4"
							onClick={handleCancel}
							disabled={loader}
						/>
						<Button
							type="button"
							button="Confirm & Recalculate"
							className="btn-primary mt-0 px-4"
							onClick={onConfirmRecalculate}
							disabled={loader}
						/>
						<Button
							type="button"
							button="Don't Recalculate"
							className="btn-danger mt-0 px-4"
							onClick={onDontRecalculate}
							disabled={loader}
						/>
					</div>
				</div>
			</div>
		</div>
	);
};

SellLooseRecalculationModal.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func,
	onCancel: PropTypes.func,
	onConfirmRecalculate: PropTypes.func.isRequired,
	onDontRecalculate: PropTypes.func.isRequired,
	productName: PropTypes.string,
	productCode: PropTypes.string,
	contains: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	packagingName: PropTypes.string,
	unitName: PropTypes.string,
	currentSellLoose: PropTypes.bool,
	pendingSellLoose: PropTypes.bool,
	batches: PropTypes.array,
	loader: PropTypes.bool,
};

export default SellLooseRecalculationModal;
